import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, type ComponentType } from 'react';
import {
  HomeIcon,
  TruckIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  XMarkIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  BanknotesIcon,
  MapPinIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronDoubleLeftIcon,
  UserGroupIcon,
  ArchiveBoxIcon,
  RectangleStackIcon,
  CubeIcon,
  CakeIcon,
  CalculatorIcon,
  CircleStackIcon,
  InboxStackIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import type { Module } from '../../contexts/PermissionContext';

const SIDEBAR_COLLAPSED_KEY = 'loyal-sidebar-collapsed';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  name: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
  module?: Module;
  subItems?: {
    name: string;
    href: string;
    icon: ComponentType<{ className?: string }>;
    module?: Module;
  }[];
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    label: 'sidebarSections.main',
    items: [
      { name: 'nav.dashboard', href: '/', icon: HomeIcon, module: 'dashboard' },
      { name: 'nav.shipments', href: '/shipments', icon: TruckIcon, module: 'shipments' },
      {
        name: 'nav.shipmentTracking',
        href: '/shipments/tracking',
        icon: MapPinIcon,
        module: 'shipments',
      },
      { name: 'nav.contracts', href: '/contracts', icon: DocumentTextIcon, module: 'contracts' },
      {
        name: 'nav.products',
        href: '/products',
        icon: CubeIcon,
        module: 'products',
        subItems: [
          { name: 'nav.productsCatalog', href: '/products', icon: CubeIcon, module: 'products' },
          {
            name: 'nav.fumigationDestinations',
            href: '/products/fumigation-destinations',
            icon: ExclamationTriangleIcon,
            module: 'products',
          },
        ],
      },
      { name: 'nav.tasks', href: '/tasks', icon: ClipboardDocumentCheckIcon, module: 'dashboard' },
      { name: 'nav.companies', href: '/companies', icon: BuildingOfficeIcon, module: 'companies' },
    ],
  },
  {
    label: 'sidebarSections.financeSection',
    items: [
      { name: 'nav.finance', href: '/finance', icon: BanknotesIcon, module: 'finance' },
      { name: 'nav.accounting', href: '/accounting', icon: CalculatorIcon, module: 'accounting' },
      { name: 'nav.analytics', href: '/analytics', icon: ChartBarIcon, module: 'analytics' },
    ],
  },
  {
    label: 'sidebarSections.operations',
    items: [
      {
        name: 'nav.clearance',
        href: '/customs-clearing-costs',
        icon: ArchiveBoxIcon,
        module: 'customs',
        subItems: [
          {
            name: 'customsClearingCosts.title',
            href: '/customs-clearing-costs',
            icon: ArchiveBoxIcon,
            module: 'customs',
          },
          {
            name: 'customsClearingCosts.batches',
            href: '/customs-clearing-batches',
            icon: RectangleStackIcon,
            module: 'customs',
          },
          {
            name: 'nav.clearanceRequests',
            href: '/clearance-requests',
            icon: ClipboardDocumentCheckIcon,
            module: 'customs',
          },
          { name: 'nav.borderAgent', href: '/border-agent', icon: TruckIcon, module: 'customs' },
          { name: 'nav.efatura', href: '/e-fatura', icon: DocumentTextIcon, module: 'customs' },
        ],
      },
      {
        name: 'nav.landTransport',
        href: '/land-transport',
        icon: TruckIcon,
        module: 'land_transport',
      },
    ],
  },
  {
    label: 'sidebarSections.warehouse',
    items: [
      {
        name: 'nav.inventory',
        href: '/inventory',
        icon: InboxStackIcon,
        module: 'inventory',
        subItems: [
          {
            name: 'nav.inventoryDashboard',
            href: '/inventory',
            icon: InboxStackIcon,
            module: 'inventory',
          },
          {
            name: 'nav.warehouseStock',
            href: '/inventory/stock',
            icon: CircleStackIcon,
            module: 'inventory',
          },
          {
            name: 'nav.qualityIncidents',
            href: '/quality-incidents',
            icon: ExclamationTriangleIcon,
            module: 'quality',
          },
          {
            name: 'nav.qualityAntrepo',
            href: '/quality-antrepo',
            icon: ShieldCheckIcon,
            module: 'antrepo',
          },
        ],
      },
      {
        name: 'nav.stock',
        href: '/antrepo',
        icon: CircleStackIcon,
        module: 'antrepo',
        subItems: [
          {
            name: 'nav.antrepoDashboard',
            href: '/antrepo',
            icon: CircleStackIcon,
            module: 'antrepo',
          },
          {
            name: 'nav.elleclemeDashboard',
            href: '/ellecleme',
            icon: WrenchScrewdriverIcon,
            module: 'ellecleme',
          },
          {
            name: 'nav.loadingDispatch',
            href: '/loading/dispatch',
            icon: PaperAirplaneIcon,
            module: 'loading_dispatch',
          },
          { name: 'nav.employees', href: '/employees', icon: UserGroupIcon, module: 'hr' },
          {
            name: 'nav.antrepoDocuments',
            href: '/antrepo-documents',
            icon: DocumentTextIcon,
            module: 'antrepo_docs',
          },
          {
            name: 'nav.cargoInsurance',
            href: '/cargo-insurance',
            icon: ArchiveBoxIcon,
            module: 'cargo_insurance',
          },
          {
            name: 'nav.templates',
            href: '/templates',
            icon: ClipboardDocumentCheckIcon,
            module: 'templates',
          },
        ],
      },
      {
        name: 'nav.warehouseExpenses',
        href: '/warehouse-expenses',
        icon: BanknotesIcon,
        module: 'warehouse_expenses',
      },
    ],
  },
  {
    label: 'sidebarSections.other',
    items: [
      { name: 'nav.cafeDashboard', href: '/cafe', icon: CakeIcon, module: 'cafe' },
      { name: 'nav.cashBoxes', href: '/cashbox', icon: BanknotesIcon, module: 'cashbox' },
    ],
  },
];

function readCollapsedPreference(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

function navLinkClass(isActive: boolean, compact: boolean) {
  return clsx(
    'flex items-center rounded-lg text-sm font-medium transition-all duration-150 no-underline',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/40 focus-visible:ring-offset-1',
    // Comfortable 44px touch targets on phones, denser on desktop
    compact ? 'justify-center px-2.5 py-2.5' : 'gap-3 px-3 py-2.5 lg:py-2',
    isActive
      ? 'bg-brand-50 text-brand-800 shadow-[inset_0_0_0_1px] shadow-brand-600/10'
      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
  );
}

function adminLinkClass(isActive: boolean, compact: boolean, _tone: 'purple' | 'indigo') {
  return navLinkClass(isActive, compact);
}

function labelClass(showLabels: boolean) {
  return clsx(
    'truncate whitespace-nowrap transition-all duration-200',
    showLabels
      ? 'max-w-[11rem] opacity-100'
      : 'max-lg:max-w-[11rem] max-lg:opacity-100 lg:max-w-0 lg:opacity-0 lg:overflow-hidden'
  );
}

function sectionLabelClass(showLabels: boolean) {
  return clsx(
    'px-3 pt-5 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 transition-all duration-200',
    showLabels
      ? 'opacity-100 max-h-10'
      : 'max-lg:opacity-100 max-lg:max-h-10 lg:opacity-0 lg:max-h-0 lg:py-0 lg:overflow-hidden'
  );
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const { hasAccess } = usePermissions();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(readCollapsedPreference);
  const [isHovered, setIsHovered] = useState(false);

  const showLabels = !isCollapsed || isHovered;
  const compact = !showLabels;

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
    } catch {
      // ignore storage failures
    }
  }, [isCollapsed]);

  const toggleExpand = (itemName: string) => {
    setExpandedItems(prev =>
      prev.includes(itemName) ? prev.filter(name => name !== itemName) : [...prev, itemName]
    );
  };

  const isExpanded = (item: NavItem) => {
    if (item.subItems) {
      const hasActiveSubItem = item.subItems.some(sub => location.pathname === sub.href);
      return expandedItems.includes(item.name) || hasActiveSubItem;
    }
    return false;
  };

  const canAccessItem = (item: NavItem | { module?: Module }) => {
    if (!item.module) return true;
    return hasAccess(item.module);
  };

  const filterItems = (items: NavItem[]) =>
    items.filter(item => {
      if (item.subItems) {
        return item.subItems.some(sub => canAccessItem(sub));
      }
      return canAccessItem(item);
    });

  const filteredSections = navSections
    .map(section => ({ ...section, items: filterItems(section.items) }))
    .filter(section => section.items.length > 0);

  const closeOnMobile = () => {
    if (window.innerWidth < 1024) {
      setTimeout(() => onClose(), 0);
    }
  };

  // Close the mobile drawer with the Escape key
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-900/30 backdrop-blur-[2px] z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        onMouseEnter={() => {
          if (isCollapsed) setIsHovered(true);
        }}
        onMouseLeave={() => setIsHovered(false)}
        className={clsx(
          'fixed lg:relative inset-y-0 z-50 flex flex-col overflow-hidden',
          'transition-[width,box-shadow] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]',
          'max-lg:w-64',
          showLabels ? 'lg:w-64' : 'lg:w-[4.5rem]',
          isCollapsed && isHovered && 'lg:shadow-[0_18px_50px_-24px_rgba(0,0,0,0.35)] lg:z-[60]',
          'ltr:left-0 rtl:right-0',
          'lg:translate-x-0 lg:translate-y-0',
          isOpen
            ? 'translate-x-0 translate-y-0'
            : 'max-lg:ltr:-translate-x-full max-lg:rtl:translate-x-full',
          'bg-surface',
          'border-line ltr:border-r rtl:border-l'
        )}
      >
        <div className="flex items-center justify-between p-4 lg:hidden border-b border-line">
          <h2 className="text-lg font-bold text-brand-800">{t('app.title')}</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/40"
            aria-label={t('nav.closeMenu', 'Close navigation menu')}
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <nav
          className="p-3 space-y-0.5 relative z-50 overflow-y-auto flex-1 scrollbar-thin"
          aria-label={t('nav.mainNavigation', 'Main navigation')}
        >
          {user?.role === 'Admin' && (
            <>
              <p className={sectionLabelClass(showLabels)}>{t('sidebarSections.admin')}</p>
              <NavLink
                to="/users"
                onClick={closeOnMobile}
                title={compact ? t('nav.users', 'User Management') : undefined}
                className={({ isActive }) => adminLinkClass(isActive, compact, 'purple')}
              >
                <UserGroupIcon className="h-5 w-5 shrink-0" />
                <span className={labelClass(showLabels)}>{t('nav.users', 'User Management')}</span>
              </NavLink>
              <NavLink
                to="/admin/field-mappings"
                onClick={closeOnMobile}
                title={compact ? t('nav.fieldMappings', 'Field Mappings') : undefined}
                className={({ isActive }) => adminLinkClass(isActive, compact, 'indigo')}
              >
                <CircleStackIcon className="h-5 w-5 shrink-0" />
                <span className={labelClass(showLabels)}>
                  {t('nav.fieldMappings', 'Field Mappings')}
                </span>
              </NavLink>
              <NavLink
                to="/admin/branches"
                onClick={closeOnMobile}
                title={compact ? t('nav.branches', 'Branches') : undefined}
                className={({ isActive }) => adminLinkClass(isActive, compact, 'purple')}
              >
                <BuildingOfficeIcon className="h-5 w-5 shrink-0" />
                <span className={labelClass(showLabels)}>{t('nav.branches', 'Branches')}</span>
              </NavLink>
              <NavLink
                to="/border-crossings"
                onClick={closeOnMobile}
                title={compact ? t('nav.borderCrossings', 'Border Crossings') : undefined}
                className={({ isActive }) => adminLinkClass(isActive, compact, 'purple')}
              >
                <MapPinIcon className="h-5 w-5 shrink-0" />
                <span className={labelClass(showLabels)}>
                  {t('nav.borderCrossings', 'Border Crossings')}
                </span>
              </NavLink>
            </>
          )}

          {filteredSections.map(section => (
            <div key={section.label}>
              <p className={sectionLabelClass(showLabels)}>{t(section.label)}</p>
              {section.items.map(item => (
                <div key={item.name}>
                  {item.subItems ? (
                    <>
                      <div className={clsx('flex items-center', compact && 'justify-center')}>
                        <NavLink
                          to={item.href}
                          end={item.href === '/'}
                          onClick={closeOnMobile}
                          title={compact ? t(item.name) : undefined}
                          className={({ isActive }) =>
                            clsx(
                              navLinkClass(isActive && location.pathname === item.href, compact),
                              !compact && 'flex-1'
                            )
                          }
                        >
                          <item.icon className="h-5 w-5 shrink-0" />
                          <span className={labelClass(showLabels)}>{t(item.name)}</span>
                        </NavLink>
                        {showLabels && (
                          <button
                            onClick={() => toggleExpand(item.name)}
                            className="p-2 me-1 rounded-lg hover:bg-gray-100 text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/40"
                            aria-label={
                              isExpanded(item)
                                ? t('nav.collapseMenu', 'Collapse menu')
                                : t('nav.expandMenu', 'Expand menu')
                            }
                            aria-expanded={isExpanded(item)}
                            aria-controls={`submenu-${item.name}`}
                          >
                            {isExpanded(item) ? (
                              <ChevronUpIcon className="h-4 w-4" aria-hidden="true" />
                            ) : (
                              <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
                            )}
                          </button>
                        )}
                      </div>

                      {showLabels && isExpanded(item) && (
                        <div
                          id={`submenu-${item.name}`}
                          className="ms-8 mt-0.5 mb-1 space-y-0.5"
                          role="menu"
                          aria-label={t('nav.submenu', 'Submenu')}
                        >
                          {item.subItems
                            .filter(sub => canAccessItem(sub))
                            .map(subItem => (
                              <NavLink
                                key={subItem.name}
                                to={subItem.href}
                                onClick={closeOnMobile}
                                className={({ isActive }) =>
                                  clsx(
                                    'flex items-center gap-2 px-3 py-2 lg:py-1.5 rounded-lg text-sm transition-all duration-150 no-underline',
                                    isActive
                                      ? 'bg-brand-50 text-brand-800 font-medium'
                                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                  )
                                }
                                role="menuitem"
                              >
                                <subItem.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                                <span>{t(subItem.name)}</span>
                              </NavLink>
                            ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <NavLink
                      to={item.href}
                      end={item.href === '/' || item.href === '/shipments'}
                      onClick={closeOnMobile}
                      title={compact ? t(item.name) : undefined}
                      className={({ isActive }) => navLinkClass(isActive, compact)}
                      aria-label={t(item.name)}
                    >
                      <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                      <span className={labelClass(showLabels)}>{t(item.name)}</span>
                    </NavLink>
                  )}
                </div>
              ))}
            </div>
          ))}
        </nav>

        <div className="hidden lg:block p-2 border-t border-line">
          <button
            type="button"
            onClick={() => setIsCollapsed(prev => !prev)}
            className={clsx(
              'w-full flex items-center rounded-lg text-xs font-medium text-gray-500',
              'hover:bg-gray-100 hover:text-gray-700 transition-all duration-200',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/40',
              compact ? 'justify-center p-2.5' : 'gap-2 px-3 py-2.5'
            )}
            aria-label={
              isCollapsed
                ? t('nav.expandSidebar', 'Expand sidebar')
                : t('nav.collapseSidebar', 'Collapse sidebar')
            }
            aria-pressed={isCollapsed}
          >
            <ChevronDoubleLeftIcon
              className={clsx(
                'h-4 w-4 shrink-0 transition-transform duration-300 rtl:rotate-180',
                isCollapsed && 'rotate-180 rtl:rotate-0'
              )}
              aria-hidden="true"
            />
            <span className={labelClass(showLabels)}>
              {isCollapsed
                ? t('nav.expandSidebar', 'Expand sidebar')
                : t('nav.collapseSidebar', 'Collapse sidebar')}
            </span>
          </button>
        </div>

        <div
          className={clsx(
            'p-4 max-lg:pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-line transition-all duration-200',
            showLabels
              ? 'opacity-100'
              : 'max-lg:opacity-100 lg:opacity-0 lg:max-h-0 lg:py-0 lg:overflow-hidden'
          )}
        >
          <p className="text-[11px] text-gray-400 text-center">{t('app.subtitle')}</p>
        </div>
      </aside>
    </>
  );
}
